import { ApiRequestService, ApiUrls } from 'base/services'
import { Dictionary, Message, MessageChannel, MessagePullApiRequest } from 'base/store/state.interface'
import { EventService } from '../../base/services/EventService'
import { store } from '../../base/store'
import { FileAttachment } from './attachment/file-attachment.interface'

export class ChatService {
  static openedChannels: number[] = []
  static openedChannelsStats: Dictionary<number> = {}
  static readonly messageLimit = 10

  static sendMessageToChannel(text: string, attachments: FileAttachment[], channelId: number, replyToMessageId?: number) {
    if (attachments.length === 0) {
      return ApiRequestService.post(ApiUrls.messages.base, {
        Message: {
          text,
          message_channel_id: channelId,
          parent_id: replyToMessageId
        }
      })
    }

    // Use form data if any attachments should be uploaded
    const formData = new FormData()
    formData.append('Message[message_channel_id]', channelId.toString())
    formData.append('Message[text]', text)
    if (replyToMessageId) formData.append('Message[parent_id]', replyToMessageId.toString())

    for (let index = 0; index < attachments.length; index++) {
      const file = attachments[index].file
      if (file === undefined) {
        console.error('Error uploading attachment, no file reference provided', attachments[index])
        continue
      }

      formData.append(`Message[uploads][${index}]`, file)
    }

    return ApiRequestService.post(ApiUrls.messages.base, formData)
  }

  static deleteMessage(messageId: number) {
    return ApiRequestService.delete(`${ApiUrls.messages.base}/${messageId}`)
  }

  /**
   * Update the text of a message
   */
  static updateMessage(message: Message, text: string) {
    return ApiRequestService.put(`${ApiUrls.messages.base}/${message.id}`, {
      Message: {
        text
      }
    })
  }

  static async fetchMessages(channelId: number, firstMessageId?: number) {
    let url = `${ApiUrls.messageChannel.messages}/${channelId}?limit=${ChatService.messageLimit}`

    if (firstMessageId) {
      url += `&first_message_id=${firstMessageId}`
    }

    const reply = await ApiRequestService.get(url)
    return reply.data.data
  }

  static async fetchThread(channelId: number, threadParentId: number) {
    let url = `${ApiUrls.messageChannel.messages}/${channelId}?limit=999&parent_message_id=${threadParentId}`
    const reply = await ApiRequestService.get(url)
    return reply.data.data.items
  }

  static watchMessageCache() {
    EventService.$on('pullservice--validate-cache-message', async (data: MessagePullApiRequest) => {
      if (!data.items) {
        return
      }

      await store.dispatch('validateMessageCache', data)
    })
  }

  static getChannelMessages(channel: MessageChannel): Message[] {
    if (!channel) return []

    let messagesDict

    if (store.state.message) {
      messagesDict = store.state.message.items
    }

    let channelCache

    if (store.state.messenger) {
      channelCache = store.state.messenger.channels[channel.id]
    }

    if (!channelCache || !messagesDict) return []

    return channelCache.messageSorting.map(messageId => messagesDict[messageId]).filter(message => !!message)
  }

  static getNumUnreadMessagesInChannel(channel: MessageChannel): number | string {
    if (!channel) {
      return 0
    }

    const state = store.state
    const channelInfo = state.messenger.channels[channel.id]

    if (!channelInfo) {
      return 0
    }

    const lastReadMessageId = channel.self_access.last_message_id
    const allChannelMessages = channelInfo.messageSorting
    const maxNumUnreadMessages = parseInt(state.message.limit as unknown as string, 10)

    let numUnread = 0
    let messageIndex = allChannelMessages.length - 1

    while (
      (numUnread < maxNumUnreadMessages) &&
      (allChannelMessages[messageIndex--] > lastReadMessageId)
      ) {
      numUnread++
    }

    if (!maxNumUnreadMessages || numUnread < maxNumUnreadMessages) {
      return numUnread
    }

    return (maxNumUnreadMessages - 1) + '+'
  }

  static addOpenedChannel(channel: MessageChannel) {
    if (!channel) {
      return
    }

    const channels = this.openedChannels
    const channelId = channel.id

    this.openedChannelsStats[channelId] = (this.openedChannelsStats[channelId] || 0) + 1

    if (channels.indexOf(channelId) > -1) {
      return
    }

    channels.push(channelId)
  }

  static removeOpenedChannel(channel: MessageChannel) {
    if (!channel) {
      return
    }

    const channels = this.openedChannels
    const channelId = channel.id
    const index = channels.indexOf(channelId)

    if (index < 0) {
      return
    }

    this.openedChannelsStats[channelId] = (this.openedChannelsStats[channelId] || 1) - 1

    if (this.openedChannelsStats[channelId] > 0) {
      return
    }

    delete this.openedChannelsStats[channelId]

    channels.splice(index, 1)
  }

  static isChannelOpened(channelId: number) {
    return this.openedChannels.indexOf(channelId) > -1
  }
}
